// conversions.js — declarative question-type conversion registry.
//
// Every datagroup question type is modelled as family × dims:
//   family: Single | Numeric | Multiple      dims: 1 (flat) … 4 (rows×cols×statements)
// plus the computed types: Custom, Stacked, Volume, Share.
//
// getConversion(variable, key, targetType) returns either
//   { status: 'current' }
//   { status: 'disabled', reason }
//   { status: 'ok', label, params: [spec…], validate(v, params), apply(v, key, params, dataGroup) }
// where apply is PURE and returns a dataGroup patch:
//   { remove: [keys], add: { key: variable }, warnings: [string] }
// The caller (ConvertTypeModal / PropertiesComponent) commits the patch.

export const FAMILIES = ["Single", "Numeric", "Multiple"];
export const DIMS = [1, 2, 3, 4];
export const COMPUTED_TYPES = ["Custom", "Stacked", "Volume", "Share"];

export const AXES_BY_DIMS = { 1: [], 2: ["Rows"], 3: ["Rows", "Cols"], 4: ["Rows", "Cols", "Statements"] };
export const AXIS_FIELD = { Rows: "row", Cols: "col", Statements: "statement" };

export const SPECIAL_ROWS = {
  Stacked: { prop: "stackedRows", label: "Stacked Rows", division: false, needsValues: true },
  Volume:  { prop: "volumeRows",  label: "Volume Rows",  division: false, needsValues: false },
  Share:   { prop: "shareRows",   label: "Share Rows",   division: true,  needsValues: false },
};

export function typeName(family, dims) {
  if (family === "Single")   return dims === 1 ? "SingleResponse" : `Single${dims}DGrid`;
  if (family === "Numeric")  return dims === 1 ? "Numeric" : `Numeric${dims}DGrid`;
  if (family === "Multiple") return dims === 1 ? null : `Multiple${dims}DGrid`;
  return null;
}

export function typeMeta(type) {
  if (!type) return null;
  if (COMPUTED_TYPES.includes(type)) return { family: "Computed", dims: 0, computed: true, type };
  for (const family of FAMILIES) {
    for (const dims of DIMS) {
      if (typeName(family, dims) === type) return { family, dims, computed: false, type };
    }
  }
  return null;
}

// Expected varName count = product of axis lengths (1 for flat questions).
export function varCountStatus(variable) {
  const meta = typeMeta(variable?.type);
  const actual = (variable?.varName || []).length;
  if (!meta || meta.computed) return { expected: null, actual, ok: true };
  let expected = 1;
  for (const axis of AXES_BY_DIMS[meta.dims]) {
    expected *= (variable[axis] || []).length;
  }
  return { expected, actual, ok: expected === actual };
}

// ── helpers ────────────────────────────────────────────────────────────────

const deep = (o) => JSON.parse(JSON.stringify(o));

const fmt = (pattern, vars) =>
  String(pattern || "").replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : m));

// valueLabels keys follow the SPSS "1.0" style used elsewhere in the app.
const vlKey = (idx) => {
  const n = Number(idx);
  if (Number.isNaN(n)) return String(idx);
  return Number.isInteger(n) ? n.toFixed(1) : String(n);
};

// Parse a textarea of "1=Yes" / "1<TAB>Yes" lines into { values, valueLabels }.
export function parseValuesText(text) {
  const values = [];
  const valueLabels = {};
  const errors = [];
  String(text || "").split("\n").map((l) => l.trim()).filter(Boolean).forEach((line, i) => {
    const m = line.includes("\t") ? line.split("\t") : line.split("=");
    if (m.length < 2 || m[0].trim() === "" || Number.isNaN(Number(m[0].trim()))) {
      errors.push(`Line ${i + 1}: expected "index=label", got "${line}"`);
      return;
    }
    const index = Number(m[0].trim());
    const title = m.slice(1).join("=").trim();
    values.push({ index, title });
    valueLabels[vlKey(index)] = title;
  });
  return { values, valueLabels, errors };
}

// Key that doesn't collide with the dataGroup or keys already in the patch.
function uniqueKey(base, dataGroup, add) {
  let k = base, i = 2;
  while ((dataGroup && dataGroup[k] !== undefined) || (add && add[k] !== undefined)) k = `${base}_${i++}`;
  return k;
}

function stripAxes(v, alsoGridFlags) {
  const out = { ...v };
  delete out.Rows; delete out.Cols; delete out.Statements; delete out.tb;
  if (alsoGridFlags) { delete out.grid; delete out.reverseGrid; }
  return out;
}

const matchVnsForRow = (variable, meta, rowIndex) => {
  const vns = variable.varName || [];
  if (meta.dims <= 1) return vns;
  return vns.filter((vn) => String(vn.row) === String(rowIndex));
};

// ── conversion builders ────────────────────────────────────────────────────

function buildSplit(variable, key, fromMeta, toMeta, targetType) {
  const axes = AXES_BY_DIMS[fromMeta.dims];
  return {
    status: "ok",
    label: `Split into one ${targetType} per item of the chosen axis`,
    params: [
      {
        name: "axis", label: "Split by", type: "select", default: "Rows",
        choices: axes.map((a) => ({ value: a, label: `${a} (${(variable[a] || []).length})` })),
      },
      { name: "keyPattern",   label: "New key pattern",   type: "text", default: "{key}_{index}", help: "{key} {index} {title}" },
      { name: "titlePattern", label: "New title pattern", type: "text", default: "{Qtitle} - {title}", help: "{Qtitle} {title} {index}" },
      { name: "keepOriginal", label: "Keep original question", type: "checkbox", default: false },
    ],
    validate(v, params) {
      const errors = [];
      const items = v[params.axis] || [];
      if (!items.length) errors.push(`No ${params.axis} to split by.`);
      if (!(v.varName || []).length) errors.push("Question has no varNames.");
      const field = AXIS_FIELD[params.axis];
      const orphan = items.filter((it) => !(v.varName || []).some((vn) => String(vn[field]) === String(it.index)));
      if (orphan.length) errors.push(`${orphan.length} ${params.axis} item(s) have no matching varName (indices: ${orphan.slice(0, 5).map((o) => o.index).join(", ")}${orphan.length > 5 ? "…" : ""}).`);
      return { ok: errors.length === 0, errors };
    },
    apply(v, k, params, dataGroup) {
      const axis = params.axis;
      const field = AXIS_FIELD[axis];
      const items = v[axis] || [];
      const remaining = AXES_BY_DIMS[fromMeta.dims].filter((a) => a !== axis);
      const childAxes = AXES_BY_DIMS[toMeta.dims];
      const base = stripAxes(v, toMeta.dims === 1);
      const add = {};
      items.forEach((item) => {
        const childKey = uniqueKey(fmt(params.keyPattern, { key: k, index: item.index, title: item.title }), dataGroup, add);
        const vns = (v.varName || []).filter((vn) => String(vn[field]) === String(item.index));
        const newVns = vns.map((vn) => {
          const nv = { ...vn };
          delete nv[field];
          remaining.forEach((ax, i) => {
            const src = AXIS_FIELD[ax];
            const dst = AXIS_FIELD[childAxes[i]];
            if (src !== dst) { nv[dst] = nv[src]; delete nv[src]; }
          });
          return nv;
        });
        const qt = fmt(params.titlePattern, { Qtitle: v.Qtitle || k, title: item.title || "", index: item.index });
        const child = { ...base, type: targetType, Qtitle: qt, varName: newVns };
        if (child.tabSpectitle) child.tabSpectitle = qt;
        remaining.forEach((ax, i) => { child[childAxes[i]] = deep(v[ax] || []); });
        add[childKey] = child;
      });
      return { remove: params.keepOriginal ? [] : [key], add, warnings: [] };
    },
  };
}

function buildFactor(variable, key, fromMeta, toMeta, targetType) {
  return {
    status: "ok",
    label: "Factor the flat rows into a Rows × Cols grid",
    params: [
      {
        name: "method", label: "Detect groups by", type: "select", default: "pattern",
        choices: [
          { value: "pattern",   label: "Row index pattern (e.g. “1c2” = row 1, col 2)" },
          { value: "separator", label: "Row title separator (e.g. “Statement - Brand”)" },
        ],
      },
      { name: "separator", label: "Title separator", type: "text", default: " - ", help: "Used to split titles; first part → Row, second → Col" },
    ],
    validate(v, params) {
      const { errors } = factorPlan(v, params);
      return { ok: errors.length === 0, errors };
    },
    apply(v, k, params) {
      const { rows, cols, vnMap, errors } = factorPlan(v, params);
      if (errors.length) return { remove: [], add: { [k]: v }, warnings: errors };
      const newVarName = (v.varName || []).map((vn) => {
        const rc = vnMap[String(vn.row)];
        if (!rc) return vn;
        return { ...vn, row: rc.r, col: rc.c };
      });
      const updated = { ...v, type: targetType, Rows: rows, Cols: cols, varName: newVarName, grid: true, reverseGrid: true };
      if (fromMeta.family === "Multiple") {
        updated.values = [{ index: 0, title: "No" }, { index: 1, title: "Yes" }];
        updated.valueLabels = { "0.0": "No", "1.0": "Yes" };
      }
      return { remove: [], add: { [k]: updated }, warnings: [] };
    },
  };
}

// Shared planning step for 2D→3D: figure out rows/cols and a rowIndex→(r,c) map.
function factorPlan(v, params) {
  const errors = [];
  const items = v.Rows || [];
  if (!items.length) return { errors: ["No rows to factor."], rows: [], cols: [], vnMap: {} };
  const rowTitles = new Map(); // r → title
  const colTitles = new Map(); // c → title
  const vnMap = {};            // old flat index → { r, c }
  const seen = new Set();

  if (params.method === "pattern") {
    for (const item of items) {
      const m = String(item.index ?? "").match(/^(\d+)[cC](\d+)$/);
      if (!m) { errors.push(`Row index "${item.index}" doesn't match the pattern <row>c<col>.`); continue; }
      const [r, c] = [m[1], m[2]];
      if (seen.has(`${r}|${c}`)) errors.push(`Duplicate cell ${r}c${c}.`);
      seen.add(`${r}|${c}`);
      const parts = String(item.title || "").split(params.separator || " - ");
      if (!rowTitles.has(r)) rowTitles.set(r, parts.length >= 2 ? parts[0].trim() : "");
      if (!colTitles.has(c)) colTitles.set(c, parts.length >= 2 ? parts.slice(1).join(params.separator).trim() : "");
      vnMap[String(item.index)] = { r, c };
    }
  } else {
    const sep = params.separator || " - ";
    for (const item of items) {
      const parts = String(item.title || "").split(sep);
      if (parts.length < 2) { errors.push(`Row "${item.title}" has no "${sep}" separator.`); continue; }
      const rowT = parts[0].trim();
      const colT = parts.slice(1).join(sep).trim();
      if (!rowTitles.has(rowT)) rowTitles.set(rowT, rowT);
      if (!colTitles.has(colT)) colTitles.set(colT, colT);
      const r = String([...rowTitles.keys()].indexOf(rowT) + 1);
      const c = String([...colTitles.keys()].indexOf(colT) + 1);
      if (seen.has(`${r}|${c}`)) errors.push(`Duplicate cell "${rowT}" × "${colT}".`);
      seen.add(`${r}|${c}`);
      vnMap[String(item.index)] = { r, c };
    }
  }

  const nr = rowTitles.size, nc = colTitles.size;
  if (!errors.length && nr * nc !== items.length) {
    errors.push(`Detected ${nr} rows × ${nc} cols = ${nr * nc}, but the question has ${items.length} rows — grid isn't complete.`);
  }
  const sortNum = (a, b) => {
    const ai = a.index === "99" ? 100 : parseInt(a.index, 10);
    const bi = b.index === "99" ? 100 : parseInt(b.index, 10);
    return ai - bi;
  };
  let rows, cols;
  if (params.method === "pattern") {
    rows = [...rowTitles.entries()].map(([index, title]) => ({ index, title })).sort(sortNum);
    cols = [...colTitles.entries()].map(([index, title]) => ({ index, title })).sort(sortNum);
  } else {
    rows = [...rowTitles.keys()].map((t, i) => ({ index: String(i + 1), title: t }));
    cols = [...colTitles.keys()].map((t, i) => ({ index: String(i + 1), title: t }));
  }
  return { errors, rows, cols, vnMap };
}

function buildFamilyChange(variable, key, fromMeta, toMeta, targetType) {
  const toFamily = toMeta.family;
  const params = [];
  if (toFamily === "Single" && fromMeta.family === "Numeric") {
    params.push({
      name: "valuesText", label: "Values (one per line: index=label)", type: "textarea",
      default: "", placeholder: "1=Yes\n2=No\n99=Don't know",
      help: "Leave empty to reuse previously saved values, if any.",
    });
  }
  const labels = {
    Numeric: "Drop values/valueLabels (kept internally for undo) and treat data as numeric",
    Single: "Keep structure, attach values & valueLabels",
    Multiple: "Keep structure, force binary 0/1 values (No/Yes)",
  };
  return {
    status: "ok",
    label: labels[toFamily],
    params,
    validate(v, p) {
      const errors = [];
      if (toFamily === "Single" && fromMeta.family === "Numeric") {
        const hasSource = (p.valuesText || "").trim() || v._savedValues || (v.values || []).length;
        if (!hasSource) errors.push("Provide values (index=label lines) — a Single question needs them.");
        if ((p.valuesText || "").trim()) {
          const parsed = parseValuesText(p.valuesText);
          errors.push(...parsed.errors);
        }
      }
      return { ok: errors.length === 0, errors };
    },
    apply(v, k, p) {
      const out = { ...v, type: targetType };
      const warnings = [];
      if (toFamily === "Numeric") {
        if (out.values) { out._savedValues = out.values; delete out.values; }
        if (out.valueLabels) { out._savedValueLabels = out.valueLabels; delete out.valueLabels; }
      } else if (toFamily === "Multiple") {
        if (out.values && JSON.stringify((out.values || []).map((x) => Number(x.index)).sort()) !== "[0,1]") {
          out._savedValues = out.values;
          out._savedValueLabels = out.valueLabels;
          warnings.push("Existing values replaced with binary 0/1 (No/Yes); originals kept internally.");
        }
        out.values = [{ index: 0, title: "No" }, { index: 1, title: "Yes" }];
        out.valueLabels = { "0.0": "No", "1.0": "Yes" };
      } else if (toFamily === "Single") {
        if ((p.valuesText || "").trim()) {
          const parsed = parseValuesText(p.valuesText);
          out.values = parsed.values;
          out.valueLabels = parsed.valueLabels;
        } else if (!(out.values || []).length && out._savedValues) {
          out.values = out._savedValues;
          out.valueLabels = out._savedValueLabels || {};
          delete out._savedValues; delete out._savedValueLabels;
          warnings.push("Restored previously saved values.");
        }
      }
      return { remove: [], add: { [k]: out }, warnings };
    },
  };
}

function buildToCustom(variable, key, fromMeta) {
  return {
    status: "ok",
    label: "Convert to Custom — rows become logic expressions you can edit",
    params: [],
    validate() { return { ok: true, errors: [] }; },
    apply(v, k) {
      const vn0 = (v.varName || [])[0]?.Name || k;
      const customRows = (v.values || []).length
        ? v.values.map((val) => ({
            index: Number(val.index), label: val.title || String(val.index),
            logic: `${vn0}=${val.index}`, logicBase: "", norank: false, fix: false,
          }))
        : [{ index: 1, label: "", logic: "", logicBase: "", norank: false, fix: false }];
      const out = { ...stripAxes(v, true), type: "Custom", customRows };
      // Backend renders values rows AND customRows together — the customRows now
      // replicate the values as logic, so stash values to avoid duplicate rows.
      if (out.values) { out._savedValues = out.values; delete out.values; }
      if (out.valueLabels) { out._savedValueLabels = out.valueLabels; delete out.valueLabels; }
      return { remove: [], add: { [k]: out }, warnings: [] };
    },
  };
}

// Parse punch lines for grid → Custom. One line = one new Custom question.
// A line is a punch set like "1,2" or "1-4,5", with an optional label after "=":
// "1,2=Top 2 Box". Returns { lines: [{punches, label}], errors: [] }.
export function parsePunchLines(text) {
  const lines = [];
  const errors = [];
  String(text || "").split("\n").map((l) => l.trim()).filter(Boolean).forEach((raw, i) => {
    const eq = raw.indexOf("=");
    const punches = (eq >= 0 ? raw.slice(0, eq) : raw).replace(/\s+/g, "");
    const label = eq >= 0 ? raw.slice(eq + 1).trim() : "";
    if (!/^\d+(\.\d+)?(-\d+(\.\d+)?)?(,\d+(\.\d+)?(-\d+(\.\d+)?)?)*$/.test(punches)) {
      errors.push(`Line ${i + 1}: "${raw}" — punches must be codes/ranges like "1,2" or "1-4,5" (optional label after "=").`);
      return;
    }
    lines.push({ punches, label });
  });
  if (!lines.length && !errors.length) errors.push("Enter at least one punch line.");
  return { lines, errors };
}

// Single2DGrid / Numeric2DGrid → Custom: one Custom question per punch line.
// Every grid row becomes a customRow with logic <row varName>=<punches>.
function buildGridToCustom(variable, key, fromMeta) {
  return {
    status: "ok",
    label: "Create one Custom question per punch line — each grid row becomes a custom row with logic varName=punches",
    params: [
      {
        name: "punchesText", label: "Punch codes (one Custom question per line)", type: "textarea",
        default: "", placeholder: "1,2=Top 2 Box\n4,5=Bottom 2 Box\n1-4,99",
        help: "Each line makes one Custom question. Row logic: <varName>=<punches>. Optional label after \"=\".",
      },
      { name: "keyPattern",   label: "New key pattern",   type: "text", default: "{key}_C{n}", help: "{key} {n} {label} {punches}" },
      { name: "titlePattern", label: "New title pattern", type: "text", default: "{Qtitle} - {label}", help: "{Qtitle} {label} {punches} {n}" },
      { name: "keepOriginal", label: "Keep original question", type: "checkbox", default: true },
    ],
    validate(v, params) {
      const errors = [];
      if (!(v.Rows || []).length) errors.push("Question has no Rows.");
      if (!(v.varName || []).length) errors.push("Question has no varNames.");
      const orphan = (v.Rows || []).filter(
        (r) => !(v.varName || []).some((vn) => String(vn.row) === String(r.index))
      );
      if (orphan.length) errors.push(`${orphan.length} row(s) have no matching varName (indices: ${orphan.slice(0, 5).map((o) => o.index).join(", ")}${orphan.length > 5 ? "…" : ""}).`);
      errors.push(...parsePunchLines(params.punchesText).errors);
      return { ok: errors.length === 0, errors };
    },
    apply(v, k, params, dataGroup) {
      const { lines } = parsePunchLines(params.punchesText);
      const add = {};
      const warnings = [];
      lines.forEach((line, i) => {
        const n = i + 1;
        const labelText = line.label || line.punches;
        const newKey = uniqueKey(
          fmt(params.keyPattern, { key: k, n, label: labelText, punches: line.punches }),
          dataGroup, add
        );
        const customRows = (v.Rows || []).map((r, ri) => {
          const vn = (v.varName || []).find((x) => String(x.row) === String(r.index));
          return {
            index: Number(r.index) || ri + 1,
            label: r.title || String(r.index),
            logic: `${vn?.Name || k}=${line.punches}`,
            logicBase: "", norank: false, fix: false,
          };
        });
        const qt = fmt(params.titlePattern, { Qtitle: v.Qtitle || k, label: labelText, punches: line.punches, n });
        const child = { ...stripAxes(v, true), type: "Custom", customRows, Qtitle: qt };
        if (child.tabSpectitle) child.tabSpectitle = qt;
        // values rows would render alongside customRows — stash them.
        if (child.values) { child._savedValues = child.values; delete child.values; }
        if (child.valueLabels) { child._savedValueLabels = child.valueLabels; delete child.valueLabels; }
        add[newKey] = child;
      });
      return { remove: params.keepOriginal ? [] : [key], add, warnings };
    },
  };
}

function buildToSpecial(variable, key, fromMeta, targetType) {
  const cfg = SPECIAL_ROWS[targetType];
  return {
    status: "ok",
    label: {
      Stacked: "Each row becomes a stacked table: its variables are clubbed and frequencies run per value",
      Volume:  "Each row becomes a sum of its variables — one Volume table",
      Share:   "Like Volume, plus a division (denominator) per row — one Share table",
    }[targetType],
    params: [],
    validate(v) {
      const errors = [];
      if (!(v.varName || []).length) errors.push("Question has no varNames.");
      if (cfg.needsValues && !(v.values || []).length) {
        errors.push("Stacked needs values/valueLabels — convert to a Single type first or add values.");
      }
      return { ok: errors.length === 0, errors };
    },
    apply(v, k) {
      const srcRows = (v.Rows || []).length ? v.Rows : [{ index: 1, title: v.Qtitle || k }];
      const meta = typeMeta(v.type) || fromMeta;
      // Reuse rows from a sibling computed type when converting between them.
      let baseRows = null;
      for (const t of Object.keys(SPECIAL_ROWS)) {
        if (t !== targetType && (v[SPECIAL_ROWS[t].prop] || []).length) { baseRows = v[SPECIAL_ROWS[t].prop]; break; }
      }
      const rows = baseRows
        ? baseRows.map((r) => ({ index: r.index, label: r.label, varNames: deep(r.varNames || []) }))
        : srcRows.map((r) => ({
            index: r.index, label: r.title || String(r.index),
            varNames: matchVnsForRow(v, meta, r.index).map((vn) => vn.Name),
          }));
      if (cfg.division) rows.forEach((r) => { r.divisionLogic = r.divisionLogic || ""; });
      let out = { ...stripAxes(v, true), type: targetType, [cfg.prop]: rows };
      // Remove sibling special-row props so only one set drives the tables.
      Object.values(SPECIAL_ROWS).forEach(({ prop }) => { if (prop !== cfg.prop) delete out[prop]; });
      if (!cfg.needsValues) {
        if (out.values) { out._savedValues = out.values; delete out.values; }
        if (out.valueLabels) { out._savedValueLabels = out.valueLabels; delete out.valueLabels; }
      } else if (!(out.values || []).length && out._savedValues) {
        out.values = out._savedValues; out.valueLabels = out._savedValueLabels || {};
        delete out._savedValues; delete out._savedValueLabels;
      }
      if (cfg.division && out.defaultDivision === undefined) out.defaultDivision = "";
      return { remove: [], add: { [k]: out }, warnings: [] };
    },
  };
}

// ── public API ─────────────────────────────────────────────────────────────

export function getConversion(variable, key, targetType) {
  const fromType = variable?.type;
  if (!fromType) return { status: "disabled", reason: "Question has no type." };
  if (targetType === fromType) return { status: "current" };

  const fromMeta = typeMeta(fromType);
  const toMeta = typeMeta(targetType);
  if (!fromMeta) return { status: "disabled", reason: `Unknown current type "${fromType}" — set a known type first.` };
  if (!toMeta) return { status: "disabled", reason: "Not a valid type." };

  // ── targets that are computed types ──
  if (toMeta.computed) {
    if (fromMeta.computed) {
      if (targetType === "Custom" || fromType === "Custom") {
        return { status: "disabled", reason: "Custom ↔ other computed types isn't supported — Custom rows are free logic." };
      }
      return buildToSpecial(variable, key, fromMeta, targetType); // Stacked/Volume/Share between each other
    }
    if (targetType === "Custom") {
      if (fromMeta.dims === 1) return buildToCustom(variable, key, fromMeta);
      if (fromMeta.dims === 2 && (fromMeta.family === "Single" || fromMeta.family === "Numeric")) {
        return buildGridToCustom(variable, key, fromMeta);
      }
      return { status: "disabled", reason: fromMeta.family === "Multiple"
        ? "Multiple grids are already binary — use the Custom Rows tab + Save Variable to merge logic rows instead."
        : "Convert 3D/4D grids to Custom after splitting them down to 2D or single questions." };
    }
    return buildToSpecial(variable, key, fromMeta, targetType);
  }

  // ── from computed types back to structured ──
  if (fromMeta.computed) {
    return { status: "disabled", reason: fromType === "Custom"
      ? "Use “Save Variable” to materialize Custom rows as a Multiple2DGrid with real SPSS variables."
      : "Computed types can't be converted back to structured types (the varName↔axis mapping was discarded)." };
  }

  // ── structured → structured ──
  const dDims = toMeta.dims - fromMeta.dims;

  if (dDims === 0) return buildFamilyChange(variable, key, fromMeta, toMeta, targetType);

  if (dDims === -1) {
    const sameFamily = toMeta.family === fromMeta.family;
    const multiToSingle1D = fromMeta.family === "Multiple" && targetType === "SingleResponse";
    if (sameFamily || multiToSingle1D) return buildSplit(variable, key, fromMeta, toMeta, targetType);
    return { status: "disabled", reason: "Split keeps the family — split within the family first, then change family." };
  }

  if (dDims === 1) {
    if (toMeta.family !== fromMeta.family) return { status: "disabled", reason: "Change family separately (same dimensionality) before/after restructuring." };
    if (fromMeta.dims === 1) return { status: "disabled", reason: "Merging separate questions into a grid isn't supported yet." };
    if (fromMeta.dims === 2) return buildFactor(variable, key, fromMeta, toMeta, targetType);
    return { status: "disabled", reason: "3D → 4D factoring isn't supported yet." };
  }

  if (dDims < -1) return { status: "disabled", reason: "Reduce one dimension at a time (split twice)." };
  return { status: "disabled", reason: "Increase one dimension at a time." };
}

// Matrix description for the modal: every possible target with its status.
export function getTargets(variable, key) {
  const cells = [];
  for (const family of FAMILIES) {
    for (const dims of DIMS) {
      const t = typeName(family, dims);
      cells.push({
        family, dims, type: t,
        result: t ? getConversion(variable, key, t) : { status: "disabled", reason: "Type doesn't exist." },
      });
    }
  }
  const computed = COMPUTED_TYPES.map((t) => ({ type: t, result: getConversion(variable, key, t) }));
  return { cells, computed };
}

export function duplicateVariable(variable, key, dataGroup) {
  const newKey = uniqueKey(`${key}_copy`, dataGroup, {});
  return { remove: [], add: { [newKey]: deep(variable) }, warnings: [], newKey };
}
